import React from "react";
import moment from "moment";
import { Http } from "utils/Http";
import {
  WaitScreen,
  SuccessMessage,
  ValidationSummary,
  Wizard,
  Step,
  ConfirmationModal,
  DataTableState,
  Select,
  RowCol,
} from "@countysuite/ui";
import { LackOfProsecutionCaseResultsPanel } from "./CaseResultsPanel/LackOfProsecutionCaseResultsPanel";
import { LackOfProsecutionEditDefinitionPanel } from "./EditDefinitionPanel/LackOfProsecutionEditDefinitionPanel";
import {
  LackOfProsecutionEditDefinitionViewModel,
  LackOfProsecutionCaseSearchResult,
  LackOfProsecutionTemplate,
  EditLOPDefinitionResponse,
  LackOfProsecutionAddCasesCommand,
  LackOfProsecutionAddCasesResponse,
  DoCaseSearchCommand,
  GetCasesCommand,
  LackOfProsecutionGetCasesResponse,
  GenerateLettersResponse,
  GenerateLettersCommand,
  UndoLettersCommand,
  UndoLettersResponse,
  CombineLettersCommand,
  CombineLettersResponse,
  DispositionCategoryType,
  DoCaseSearchResponse,
} from "./LackOfProsecutionTypes";
import {
  CaseEventCategory,
  DropdownCategory,
  RequestCategorySubCategory,
  Official,
  GetCaseIDsByDefintionResponse,
} from "types/CivilTypes";
import { containSameElements } from "utils/containSameElements";
import { LackOfProsecutionGenerateLettersPanel } from "./GenerateLettersPanel/LackOfProsecutionGenerateLettersPanel";
import { TsJobProgressModal } from "components/modals/TsJobProgressModal/TsJobProgressModal";
import { ClearBackgroundJobCommand, ClearBackgroundJobResponse, GetJobProgressResponse } from "components/modals/TsJobProgressModal/BackgroundJobs";
import { LackOfProsecutionSummaryPanel } from "./SummaryPanel/LackOfProsecutionSummaryPanel";
import "./LackOfProsecution.scss";

export interface Props extends LackOfProsecutionEditDefinitionViewModel {
  initialReferenceDate?: string;
  lopTemplates: LackOfProsecutionTemplate[];

  casesInTable: any[];

  caseEventCategories: CaseEventCategory[];
  caseCategories: RequestCategorySubCategory[];
  casePageUrl: string;
  contactPageUrl: string;
  exportToCsvUrl: string;
  indexPageUrl: string;
  jobKey?: string;

  letterCategories: DropdownCategory[];
  signatures: DropdownCategory[];
}

interface State extends LackOfProsecutionEditDefinitionViewModel {
  jobKey: string;
  jobMessage: string;
  waitMessage: string;
  modalName: string;
  saveMessage: string;
  serverErrors?: string[];

  showExcludedCases: boolean;
  hasLetters: boolean;

  /** did the user change any of the default category values? */
  hasCustomizedCategory?: boolean;
  confirmLopTemplateID: number;
  pendingCaseCategoryChanges: boolean;
  pendingActivityChanges: boolean;
  pendingExclusionChanges: boolean;
  pendingInclusionChanges: boolean;

  casesInTable: LackOfProsecutionCaseSearchResult[];
  totalPages?: number;
  totalRows?: number;
  caseTablePagination: DataTableState;

  caseCategories: RequestCategorySubCategory[];
  caseEventCategories: CaseEventCategory[];

  lopTemplates: LackOfProsecutionTemplate[];
  letterCategories: DropdownCategory[];
  signatures: DropdownCategory[];
  defaultSignatureId: number;
  hideDeadlineDate: boolean;
  referenceDateLabel: string;
  showParticipantListButton: boolean;
  showLettersSelect: boolean;
  caseIds: number[];
  showPretrialDatePicker: boolean;
  showOnlyDocketedCases: boolean;
}

/**
 * Checks if the user modified the selected category in any way.
 * @param selectedCategory the category the user selected
 * @param caseCategoryIDs the case category IDs that the user selected
 * @param activityIgnoreList the ignored docket types that the user selected
 * @param exclusionList the docket types that disqualify a case
 */
function isCustomizedFromCategory(
  selectedCategory: LackOfProsecutionTemplate,
  caseCategoryIDs: string[],
  activityIgnoreList: number[],
  exclusionList: number[],
  inclusionList: number[]
) {
  if (
    !selectedCategory ||
    !caseCategoryIDs ||
    !activityIgnoreList ||
    !exclusionList ||
    !inclusionList
  )
    return false;

  const {
    defaultActivityIgnoreListIDs,
    defaultCaseCategoryIDs,
    defaultExclusionListIDs,
    defaultInclusionListIDs,
  } = selectedCategory;

  //TODO: handle composite key, ID is a string now for CaseCategories, because its RequestCategoryID-RequestSubCategoryID
  if (!containSameElements(caseCategoryIDs, defaultCaseCategoryIDs))
    return true;
  if (!containSameElements(activityIgnoreList, defaultActivityIgnoreListIDs))
    return true;
  if (!containSameElements(exclusionList, defaultExclusionListIDs)) return true;
  if (!containSameElements(inclusionList, defaultInclusionListIDs)) return true;

  return false;
}

export class LackOfProsecutionEditPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = this.resetState(props);
  }

  //rip the server-supplied props off of props, and put them on state.
  resetState = (props: Props): State => {
    const {
      description,
      notes,
      definitionID,
      lastCompletedStep,
      lopTemplateID: lopCategoryID,
      jobKey,
    } = props;
    const {
      selectedCaseCategoryIDs,
      selectedActivityIgnoreListIDs,
      selectedExclusionListIDs,
      selectedInclusionListIDs,
      caseCategories,
      caseEventCategories,
    } = props;
    const selectedLopCategory = this.props.lopTemplates.find(
      (lc) => lc.id === lopCategoryID
    );
    let hasCustomizedCategory = false;
    if (selectedLopCategory) {
      hasCustomizedCategory = isCustomizedFromCategory(
        selectedLopCategory,
        selectedCaseCategoryIDs,
        selectedActivityIgnoreListIDs,
        selectedExclusionListIDs,
        selectedInclusionListIDs
      );
    }

    const defaultCategory = { id: 0, name: "< Select >" };

    let lopCategories = [...props.lopTemplates];
    lopCategories.unshift({
      ...defaultCategory,
      defaultActivityIgnoreListIDs: [],
      defaultExclusionListIDs: [],
      defaultCaseCategoryIDs: [],
      defaultInclusionListIDs: [],
      defaultLettersIDs: [],
      defaultDaysOfInactivity: 0,
      sequence: 0,
      defaultSignatureOfficialID: -1,
      excludeZeroDollarJudgments: false,
      dispositionCategoryID: 0,
      dispositionTypeID: 0,
      hideDeadlineDate: false,
      referenceDateLabel: "",
      showParticipantListButton: false,
      showLettersSelect: false,
      showPretrialDatePicker: false,
    });

    let initialReferenceDate: moment.Moment | null;
    if (props.initialReferenceDate) {
      initialReferenceDate = moment(props.initialReferenceDate, "MM/DD/YYYY");
    } else {
      initialReferenceDate = null;
    }

    let signatures = [...props.signatures];
    let defaultSignatureId =
      selectedLopCategory?.defaultSignatureOfficialID ?? 1;
    let hideDeadlineDate = selectedLopCategory?.hideDeadlineDate ?? false;
    let referenceDateLabel = selectedLopCategory?.referenceDateLabel ?? "";
    let showParticipantListButton =
      selectedLopCategory?.showParticipantListButton ?? false;
    let defaultLettersIDs = selectedLopCategory?.defaultLettersIDs ?? [];

    let letterCategories = [...props.letterCategories];

    let filteredLetters = letterCategories.filter(function (category) {
      return defaultLettersIDs.indexOf(category.id) !== -1;
    });

    // filteredLetters = filteredLetters.length !== 0 ? filteredLetters : letterCategories;
    filteredLetters.unshift({
      ...defaultCategory,
    });

    let showLettersSelect = selectedLopCategory?.showLettersSelect ?? false;
    let showPretrialDatePicker =
      selectedLopCategory?.showPretrialDatePicker ?? false;
    let caseIds = [] as number[];

    return {
      definitionID: definitionID || 0,
      description: description || "",
      notes: notes || "",
      modalName: "",
      saveMessage: "",
      casesInTable: props.casesInTable || [],
      caseTablePagination: {
        pageSize: 10,
        page: 0,
        filtered: [],
        sorted: [],
      },
      hasLetters: false,
      showExcludedCases: false,
      totalPages: 0,
      lastCompletedStep: lastCompletedStep || 0,
      waitMessage: "",
      referenceDate: initialReferenceDate,
      lopTemplateID: lopCategoryID || 0,
      notificationDate: moment(),
      deadlineDate: moment().add(30, "days"),
      jobKey: jobKey || "",
      jobMessage: "",

      confirmLopTemplateID: 0,
      hasCustomizedCategory,

      pendingCaseCategoryChanges: false,
      pendingActivityChanges: false,
      pendingExclusionChanges: false,
      pendingInclusionChanges: false,

      caseCategories,
      caseEventCategories,
      lopTemplates: lopCategories,
      letterCategories: filteredLetters,
      selectedCaseCategoryIDs: selectedCaseCategoryIDs || [],
      selectedActivityIgnoreListIDs: selectedActivityIgnoreListIDs || [],
      selectedExclusionListIDs: selectedExclusionListIDs || [],
      selectedInclusionListIDs: selectedInclusionListIDs || [],
      signatures,
      defaultSignatureId,
      hideDeadlineDate,
      referenceDateLabel,
      showParticipantListButton,
      showLettersSelect,
      caseIds,
      showPretrialDatePicker,
      showOnlyDocketedCases: false
    };
  };

  componentDidMount = async () => {
    if (this.state.jobKey) {
      this.checkJobStatus();
    }
    this.setState({ caseIds: await this.getCaseIdsByDefintion() });

  };

  checkJobStatus = async () => {
    const data = {
      jobKey: this.state.jobKey || "LOP-" + this.state.definitionID,
    };
    const res = await Http.get<GetJobProgressResponse>({
      url: "BackgroundJobs/GetJobProgress",
      data,
    });
    if (res.success) {
      if (res.info.isDone || res.info.isCompleted) {
        this.setState({ jobKey: "", jobMessage: "" });
      } else {
        this.setState({
          jobKey: res.info.jobKey,
          jobMessage: res.info.jobLabel || "",
        });
      }
    } else {
      let newState: any = { jobKey: "" };
      if (res.info.errorMessage) {
        newState.serverErrors = [res.info.errorMessage];
      }
      this.setState(newState);
      await this.fetchCasesInTable();
    }
  };

  addLetterSelection = (id: number) => {
    let previousState = [...this.state.caseIds];
    previousState.push(id);
    this.setState({ caseIds: previousState });
  };

  removeLettersSelection = (id: number) => {
    let previousState = this.state.caseIds;
    let newState = previousState.filter(function (e) {
      return e !== id;
    });
    this.setState({ caseIds: newState });
  };

  getViewModel = () => {
    const vm: LackOfProsecutionEditDefinitionViewModel = {
      definitionID: this.state.definitionID,
      description: this.state.description,
      notes: this.state.notes,
      lopTemplateID: this.state.lopTemplateID,
      referenceDate: this.state.referenceDate,
      notificationDate: this.state.notificationDate,
      deadlineDate: this.state.deadlineDate,
      lastCompletedStep: this.state.lastCompletedStep,
      selectedCaseCategoryIDs: this.state.selectedCaseCategoryIDs,
      selectedActivityIgnoreListIDs: this.state.selectedActivityIgnoreListIDs,
      selectedExclusionListIDs: this.state.selectedExclusionListIDs,
      selectedInclusionListIDs: this.state.selectedInclusionListIDs,
      referenceDateLabel: this.state.referenceDateLabel,
      showLettersSelect: this.state.showLettersSelect,
    };

    return vm;
  };

  saveToServer = async (controllerUrl: string, data: any): Promise<boolean> => {
    this.setState({ waitMessage: "Saving, please wait...", saveMessage: "" });
    try {
      const saveResponse = await Http.post<EditLOPDefinitionResponse>({
        url: controllerUrl,
        data: data,
      });

      if (saveResponse.success) {
        this.setState({
          saveMessage: saveResponse.message || "Save successful.",
        });
        return true;
      } else {
        this.setState({ serverErrors: saveResponse.errors || [] });
      }
    } catch (err) {
      this.setState({
        serverErrors: [
          "An unexpected error has occurred.  Please reload CountySuite.",
        ],
      });
    } finally {
      this.setState({ waitMessage: "" });
    }

    return false;
  };

  updateViewModel = (newState: LackOfProsecutionEditDefinitionViewModel) => {
    this.setState(newState);
  };

  promptBeforeChangingLopCategory = (
    selectedCategory: LackOfProsecutionTemplate
  ) => {
    if (
      this.state.hasCustomizedCategory &&
      this.state.casesInTable.length > 0
    ) {
      this.setState({ confirmLopTemplateID: selectedCategory.id });
    } else {
      this.updateLopCategory(selectedCategory);
    }
  };

  confirmLopCategory = () => {
    const confirmedCategory = this.props.lopTemplates.find(
      (lc) => lc.id === this.state.confirmLopTemplateID
    );
    if (confirmedCategory) {
      this.updateLopCategory(confirmedCategory);
    }
  };

  updateLopCategory = (selectedCategory: LackOfProsecutionTemplate) => {
    const {
      defaultCaseCategoryIDs,
      defaultActivityIgnoreListIDs,
      defaultExclusionListIDs,
      defaultSignatureOfficialID,
      referenceDateLabel,
      showLettersSelect,
      defaultLettersIDs,
      showPretrialDatePicker,
    } = selectedCategory;

    const defaultCategory = { id: 0, name: "< Select >" };
    let filteredLetters = this.props.letterCategories.filter(function (
      category
    ) {
      return defaultLettersIDs.indexOf(category.id) !== -1;
    });
    //filteredLetters = filteredLetters.length !== 0 ? filteredLetters : this.props.letterCategories;
    filteredLetters.unshift({
      ...defaultCategory,
    });

    this.setState({
      hasCustomizedCategory: false,
      confirmLopTemplateID: 0,
      lopTemplateID: selectedCategory.id,
      referenceDate: moment().subtract(
        selectedCategory.defaultDaysOfInactivity,
        "days"
      ),
      selectedCaseCategoryIDs: [...defaultCaseCategoryIDs],
      selectedActivityIgnoreListIDs: [...defaultActivityIgnoreListIDs],
      selectedExclusionListIDs: [...defaultExclusionListIDs],
      defaultSignatureId: defaultSignatureOfficialID,
      referenceDateLabel: referenceDateLabel,
      showLettersSelect: showLettersSelect,
      letterCategories: filteredLetters,
      showPretrialDatePicker: showPretrialDatePicker,
    });
  };

  addCasesToTable = async (addedCases: LackOfProsecutionCaseSearchResult[]) => {
    this.setState({
      waitMessage: "Adding cases, please wait...",
      saveMessage: "",
    });

    const data: LackOfProsecutionAddCasesCommand = {
      definitionID: this.state.definitionID,
      caseIDs: addedCases.map((c) => c.caseID),
    };
    try {
      const response = await Http.post<LackOfProsecutionAddCasesResponse>({
        url: "LackOfProsecution/AddCases",
        data,
      });
      if (response.success) {
        await this.fetchCasesInTable();
        this.setState({
          saveMessage: `Added ${addedCases.length} cases successfully.`,
        });
      } else {
        this.setState({ serverErrors: response.errors });
      }
    } catch (err) {
      this.setState({
        serverErrors: [
          "An unexpected error has occurred while trying to add additional cases to the table, please try again later.",
        ],
      });
    } finally {
      this.setState({ waitMessage: "" });
    }
  };

  changeCaseCategorySelections = (selections: string[]) => {
    this.setState({
      selectedCaseCategoryIDs: [...selections],
      hasCustomizedCategory: true,
      pendingCaseCategoryChanges: true,
    });
  };

  changeActivityIgnoreListIDs = (selections: number[]) => {
    this.setState({
      selectedActivityIgnoreListIDs: [...selections],
      hasCustomizedCategory: true,
      pendingActivityChanges: true,
    });
  };

  changeExclusionListIDs = (selections: number[]) => {
    this.setState({
      selectedExclusionListIDs: [...selections],
      hasCustomizedCategory: true,
      pendingExclusionChanges: true,
    });
  };

  changeInclusionListIDs = (selections: number[]) => {
    this.setState({
      selectedInclusionListIDs: [...selections],
      hasCustomizedCategory: true,
      pendingInclusionChanges: true,
    });
  };

  toggleShowExclusions = (toggled: boolean) =>
    this.setState({ showExcludedCases: toggled }, this.fetchCasesInTable);


  toggleShowOnlyDocketedCases = (toggled: boolean) =>
    this.setState({ showOnlyDocketedCases: toggled }, this.fetchCasesInTable);

  fetchCasesInTable = async () => {
    const data: GetCasesCommand = {
      definitionID: this.state.definitionID,
      pageIndex: this.state.caseTablePagination.page,
      pageSize: this.state.caseTablePagination.pageSize,
      filtered: this.state.caseTablePagination.filtered,
      sorted: this.state.caseTablePagination.sorted,
      showExcludedCases: this.state.showExcludedCases,
      showOnlyDocketedCases: this.state.showOnlyDocketedCases
    };

    try {
      this.setState({ waitMessage: "Retrieving case data..." });
      const response = await Http.post<LackOfProsecutionGetCasesResponse>({
        url: "LackOfProsecution/GetCases",
        data,
      });

      if (response.success) {
        this.setState({
          casesInTable: response.casesInTable,
          totalPages: response.totalPages,
          totalRows: response.totalRows,
          hasLetters: response.hasLetters,
        });
      } else {
        this.setState({ serverErrors: response.errors });
      }
    } catch (err) {
      this.setState({ serverErrors: ["Unable to retrieve latest case data."] });
    } finally {
      this.setState({ waitMessage: "" });
    }
  };

  redoCaseSearchResults = async () => {
    const data: DoCaseSearchCommand = {
      definitionID: this.state.definitionID,
    };

    try {
      this.setState({
        waitMessage: "Building new list of cases, please wait...",
      });
      const response = await Http.post<DoCaseSearchResponse>({
        url: "LackOfProsecution/DoCaseSearch",
        data,
      });
      if (response.success) {
        this.setState({
          totalRows: response.totalRows,
          saveMessage: `Search is complete.  We found ${response.totalRows} cases matching your filter criteria.  Loading results...`,
        });
      } else {
        this.setState({ serverErrors: response.errors });
      }
    } catch (err) {
      this.setState({
        serverErrors: [
          "There was a problem while building the case result list.",
        ],
      });
    } finally {
      this.setState({ waitMessage: "" });
    }

    //fetch the cases
    await this.fetchCasesInTable();
  };

  toggleCaseExclusion = async (row: LackOfProsecutionCaseSearchResult) => {
    let data = {
      caseID: row.caseID,
      definitionID: this.props.definitionID,
      exclude: !row.isExcluded,
    };

    await Http.post({ url: "LackOfProsecution/ToggleCaseExclusion", data });

    await this.fetchCasesInTable();
  };

  handleJobCancel = async () => {
    if (this.state.jobKey) {
      this.setState({ waitMessage: "Cancelling current task..." });
      await Http.post({
        url: "BackgroundJobs/CancelJob",
        data: { jobKey: this.state.jobKey },
      });
      this.setState(
        { waitMessage: "", jobKey: "", jobMessage: "" },
        this.fetchCasesInTable
      );
    }
  };

  generateLetters = async (
    selectedCaseID: number | undefined,
    letterId: number,
    deadlineDate: string,
    notificationDate: string,
    signatureId: number,
    lettersIds: number[],
    pretrialDate: string
  ) => {
    if (letterId <= 0) return;

    let command: GenerateLettersCommand = {
      definitionID: this.props.definitionID,
      lackOfProsecutionLetterID: letterId,
      selectedCaseID,
      notificationDate,
      deadlineDate,
      signatureID: signatureId,
      lettersIds: lettersIds,
      pretrialConferenceDate: pretrialDate,
    };

    try {
      this.setState({ waitMessage: "Creating docket entries & letter(s)" });
      let res = await Http.post<GenerateLettersResponse>({
        url: "LackOfProsecution/GenerateLetters",
        data: command,
      });
      if (res.success) {
        this.setState({ jobKey: res.jobKey });
      } else {
        this.setState({ jobKey: "", serverErrors: res.errors });
      }
    } catch (err) {
      this.setState({
        serverErrors: [
          "There was a problem while generating letters.  Please contact support.",
        ],
      });
    } finally {
      this.setState({ waitMessage: "" });
    }
    await this.checkJobStatus();
  };

  undoLetters = async (
    selectedCaseID: number | undefined,
    letterId: number,
    lettersIds: number[]
  ) => {
    if (letterId <= 0) return;

    let command: UndoLettersCommand = {
      definitionID: this.props.definitionID,
      lackOfProsecutionLetterID: letterId,
      selectedCaseID,
      lettersIds: lettersIds,
    };

    try {
      this.setState({ waitMessage: "Undoing letter(s)" });
      await Http.post<UndoLettersResponse>({
        url: "LackOfProsecution/UndoLetters",
        data: command,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        serverErrors: [
          "There was a problem while undoing docket entries and letters.  Please contact support.",
        ],
      });
    } finally {
      this.setState({ waitMessage: "" });
    }

    await this.fetchCasesInTable();
  };

  combineLetters = async (
    letterId: number,
    deadlineDate: string,
    notificationDate: string,
    signatureId: number,
    letterIds: number[],
    pretrialDate: string
  ) => {
    if (letterId <= 0) return;
    let command: CombineLettersCommand = {
      definitionID: this.props.definitionID,
      lackOfProsecutionLetterID: letterId,
      deadlineDate: deadlineDate,
      notificationDate: notificationDate,
      signatureID: signatureId,
      lettersIDs: letterIds,
      pretrialConferenceDate: pretrialDate,
    };

    try {
      this.setState({ waitMessage: "Combining letter(s) into single PDF..." });
      const res = await Http.post<CombineLettersResponse>({
        url: "LackOfProsecution/CombineLetters",
        data: command,
      });
      this.setState({ jobKey: res.jobKey });
    } catch (err) {
      console.error(err);
      this.setState({
        serverErrors: [
          "There was a problem while generating your combined PDF.  Please contact support.",
        ],
      });
    } finally {
      this.setState({ waitMessage: "" });
    }

    await this.checkJobStatus();
  };

  saveDefinition = () =>
    this.saveToServer(
      "LackOfProsecution/EditLOPDefinition",
      this.getViewModel()
    );

  printParticipantsList = () => {
    let id: number = this.state.definitionID;
    Http.openNewTab(
      "LackOfProsecution/PrintParticipantsList?definitionID=" + id
    );
  };

  getCaseIdsByDefintion = async () => {
    let result = await Http.get<GetCaseIDsByDefintionResponse>({
      url:
        "LackOfProsecution/GetCaseIDs?definitionID=" + this.state.definitionID,
    });
    return result.caseIDs;
  };

  render() {
    const nextLopCategory: any = this.props.lopTemplates.find(
      (lopcat) => lopcat.id === this.state.confirmLopTemplateID
    ) || { name: "N/A" };
    return (
      <div id="lackOfProsecutionDefinition">
        <ValidationSummary errors={this.state.serverErrors || []} />

        <SuccessMessage
          show={this.state.saveMessage.length > 0}
          message={this.state.saveMessage}
          onDismiss={() => this.setState({ saveMessage: "" })}
        />

        <WaitScreen show={this.state.waitMessage.length > 0} delay={0.5}>
          {this.state.waitMessage}
        </WaitScreen>

        <ConfirmationModal
          title="Confirm Category Change"
          size="medium"
          show={this.state.confirmLopTemplateID > 0}
          onConfirm={this.confirmLopCategory}
          onCancel={() => this.setState({ confirmLopTemplateID: 0 })}
          onHide={() => this.setState({ confirmLopTemplateID: 0 })}
        >
          <p>
            You've made customizations to the Case Categories, Ignore List, or
            Exclusion List beyond the defaults for the current category.
          </p>
          <p>
            Are you sure you wish to change the category to{" "}
            <strong>{nextLopCategory.name}</strong>?
          </p>
          <p>
            This will overwrite your case categories and docket types with the
            default selections for that Category.
          </p>
        </ConfirmationModal>

        <TsJobProgressModal
          key={this.state.jobKey}
          show={this.state.jobKey.length > 0}
          jobKey={this.state.jobKey}
          onClose={(pct) => {
            if (pct < 100) {
              window.location.href = this.props.indexPageUrl;
            } else {
              var currentJobKey = this.state.jobKey;
              this.setState(
                { jobKey: "", jobMessage: "" }, () => {

                  var command: ClearBackgroundJobCommand = {
                    jobKey: currentJobKey
                  }
                  Http.post<ClearBackgroundJobResponse>({
                    url: "BackgroundJobs/ClearBackgroundJob",
                    data: command
                  });

                  this.fetchCasesInTable();
                }
              );
            }
          }}
          onJobCancel={this.handleJobCancel}
          onJobDone={this.fetchCasesInTable}
        >
          <p className="centered">
            We are creating your document(s), please wait.
          </p>
          <p className="centered">
            This page can be closed and we will continue to process the request
            in the background.
          </p>
        </TsJobProgressModal>


        <h4 className="centered">
          <strong>{this.state.description}</strong>
        </h4>
        <Wizard initialStep={this.state.lastCompletedStep + 1}>
          <Step id={1} name="Definition">
            {(stepProps) => (
              <LackOfProsecutionEditDefinitionPanel
                updateViewModel={this.updateViewModel}
                updateLopCategory={this.promptBeforeChangingLopCategory}
                onSave={this.saveDefinition}
                nextStep={stepProps.next}
                {...this.state}
              />
            )}
          </Step>
          <Step id={2} name="Case Results">
            {(stepProps) => (
              <LackOfProsecutionCaseResultsPanel
                redoCaseSearchResults={this.redoCaseSearchResults}
                reloadExistingCaseResults={(
                  page,
                  pageSize,
                  sorted,
                  filtered
                ) => {
                  this.setState(
                    {
                      caseTablePagination: { page, pageSize, sorted, filtered },
                      showOnlyDocketedCases: false
                    },
                    this.fetchCasesInTable
                  );
                }}
                addCasesToTable={this.addCasesToTable}
                nextStep={stepProps.next}
                casePageUrl={this.props.casePageUrl}
                contactPageUrl={this.props.contactPageUrl}
                toggleCaseExclusion={this.toggleCaseExclusion}
                totalPages={this.state.totalPages}
                totalRows={this.state.totalPages}
                toggleShowExclusions={this.toggleShowExclusions}
                printParticipantList={this.printParticipantsList}
                definitionId={this.props.definitionID}
                exportToCsvUrl={this.props.exportToCsvUrl}
                {...this.state}
              />
            )}
          </Step>
          <Step
            id={3}
            name="Generate Letters"
            disabled={(this.state.totalRows || 0) <= 0}
          >
            {(stepProps) => (
              <LackOfProsecutionGenerateLettersPanel
                casesInTable={this.state.casesInTable.filter(
                  (c) => !c.isExcluded
                )}
                casePageUrl={this.props.casePageUrl}
                letterCategories={this.state.letterCategories}
                generateLetters={this.generateLetters}
                combineLetters={this.combineLetters}
                undoLetters={this.undoLetters}
                totalPages={this.state.totalPages}
                totalRows={this.state.totalRows}
                reloadExistingCaseResults={(
                  page,
                  pageSize,
                  sorted,
                  filtered,
                  showOnlyDocketedCases
                ) => {
                  this.setState(
                    {
                      caseTablePagination: { page, pageSize, sorted, filtered },
                      showExcludedCases: false,
                      showOnlyDocketedCases: showOnlyDocketedCases
                    },
                    this.fetchCasesInTable
                  );
                }}
                signatures={this.props.signatures}
                defaultSignatureId={this.state.defaultSignatureId}
                hideDeadlineDate={this.state.hideDeadlineDate}
                showLetterSelect={this.state.showLettersSelect}
                caseIds={this.state.caseIds}
                addLetterID={this.addLetterSelection}
                removeLetterID={this.removeLettersSelection}
                showPretrialDatePicker={this.state.showPretrialDatePicker}
                {...stepProps}
              />
            )}
          </Step>
          <Step
            id={4}
            name="Summary"
            disabled={(this.state.totalRows || 0) <= 0}
          >
            {() => (
              <LackOfProsecutionSummaryPanel
                definitionID={this.props.definitionID}
                exportToCsvUrl={this.props.exportToCsvUrl}
              />
            )}
          </Step>
        </Wizard>
      </div>
    );
  }
}

(LackOfProsecutionEditPage as any).displayName = "LackOfProsecutionEditPage";
